import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { TranslocoService } from '@ngneat/transloco';

/**
 * Integration Error Notification Service
 * Handles error notifications for integration features
 * Uses existing ToastrService for consistent error display
 */
@Injectable({
  providedIn: 'root',
})
export class IntegrationErrorNotificationService {
  constructor(
    private toastr: ToastrService,
    private transloco: TranslocoService,
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Error Handling Methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Handle HTTP errors from integration API calls
   *
   * @param error - The HTTP error response
   */
  handleError(error: HttpErrorResponse): void {
    console.error('Integration Error:', error);

    let message = this.transloco.translate('An error occurred during integration operation');
    let title = this.transloco.translate('Integration Error');

    // Handle different error types
    if (error.error?.message) {
      message = error.error.message;
    } else if (error.error?.errors && Array.isArray(error.error.errors)) {
      // Handle validation errors array
      error.error.errors.forEach((errorMessage: string) => {
        this.toastr.error(errorMessage, title, {
          timeOut: 5000,
          progressBar: true,
          positionClass: 'toast-top-right',
        });
      });
      return;
    } else {
      // Handle specific HTTP status codes
      switch (error.status) {
        case 0:
          message = this.transloco.translate('Network error. Please check your connection.');
          break;
        case 401:
          message = this.transloco.translate(
            'Authentication failed. Please check your permissions.',
          );
          break;
        case 403:
          message = this.transloco.translate(
            'You do not have permission to perform this operation.',
          );
          break;
        case 404:
          message = this.transloco.translate(
            'Integration service not found. Please contact support.',
          );
          break;
        case 422:
          message = this.transloco.translate('Invalid data provided. Please check your input.');
          break;
        case 500:
          message = this.transloco.translate('Server error. Please try again later.');
          break;
        case 503:
          message = this.transloco.translate('Integration service is temporarily unavailable.');
          break;
        default:
          message = this.transloco.translate('An unexpected error occurred. Please try again.');
      }
    }

    this.toastr.error(message, title, {
      timeOut: 5000,
      progressBar: true,
      positionClass: 'toast-top-right',
    });
  }

  /**
   * Handle sync operation errors
   *
   * @param error - The HTTP error response
   */
  handleSyncError(error: HttpErrorResponse): void {
    console.error('Sync Error:', error);

    let message = this.transloco.translate('Project sync failed');
    const title = this.transloco.translate('Sync Error');

    if (error.error?.message) {
      message = error.error.message;
    } else if (error.status === 409) {
      message = this.transloco.translate('Another sync operation is already in progress.');
    } else if (error.status === 429) {
      message = this.transloco.translate(
        'Too many sync requests. Please wait before trying again.',
      );
    }

    this.toastr.error(message, title, {
      timeOut: 6000,
      progressBar: true,
      positionClass: 'toast-top-right',
    });
  }

  /**
   * Handle connection test errors
   *
   * @param error - The HTTP error response
   */
  handleConnectionTestError(error: HttpErrorResponse): void {
    console.error('Connection Test Error:', error);

    let message = this.transloco.translate('Connection test failed');
    const title = this.transloco.translate('Connection Error');

    if (error.error?.message) {
      message = error.error.message;
    } else if (error.status === 0) {
      message = this.transloco.translate(
        'Unable to connect to Unifier API. Please check network connection.',
      );
    } else if (error.status === 401) {
      message = this.transloco.translate('Authentication failed. Please check API credentials.');
    } else if (error.status === 404) {
      message = this.transloco.translate(
        'Unifier API endpoint not found. Please check configuration.',
      );
    }

    this.toastr.error(message, title, {
      timeOut: 6000,
      progressBar: true,
      positionClass: 'toast-top-right',
    });
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Success Notification Methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Show success notification
   *
   * @param message - Success message
   * @param title - Optional title
   */
  showSuccess(message: string, title?: string): void {
    const successTitle = title || this.transloco.translate('Success');
    this.toastr.success(message, successTitle, {
      timeOut: 3000,
      progressBar: true,
      positionClass: 'toast-top-right',
    });
  }

  /**
   * Show sync success notification
   *
   * @param created - Number of projects created
   * @param updated - Number of projects updated
   * @param matched - Number of projects matched
   * @param failed - Number of projects failed
   */
  showSyncSuccess(created: number, updated: number, matched: number, failed: number): void {
    const title = this.transloco.translate('Sync Completed Successfully');
    let message = this.transloco.translate(
      'Projects synchronized: {{created}} created, {{updated}} updated, {{matched}} matched',
      {
        created,
        updated,
        matched,
      },
    );

    if (failed > 0) {
      message += this.transloco.translate(', {{failed}} failed', { failed });
    }

    this.toastr.success(message, title, {
      timeOut: 5000,
      progressBar: true,
      positionClass: 'toast-top-right',
    });
  }

  /**
   * Show connection test success notification
   */
  showConnectionTestSuccess(): void {
    const title = this.transloco.translate('Connection Test');
    const message = this.transloco.translate('Successfully connected to Unifier API');

    this.toastr.success(message, title, {
      timeOut: 3000,
      progressBar: true,
      positionClass: 'toast-top-right',
    });
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Warning Notification Methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Show warning notification
   *
   * @param message - Warning message
   * @param title - Optional title
   */
  showWarning(message: string, title?: string): void {
    const warningTitle = title || this.transloco.translate('Warning');
    this.toastr.warning(message, warningTitle, {
      timeOut: 4000,
      progressBar: true,
      positionClass: 'toast-top-right',
    });
  }

  /**
   * Show sync warning notification for partial failures
   *
   * @param failures - Array of failure messages
   */
  showSyncWarning(failures: string[]): void {
    const title = this.transloco.translate('Sync Completed with Warnings');
    const message = this.transloco.translate(
      'Some projects failed to sync. Check sync results for details.',
    );

    this.toastr.warning(message, title, {
      timeOut: 6000,
      progressBar: true,
      positionClass: 'toast-top-right',
    });
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Info Notification Methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Show info notification
   *
   * @param message - Info message
   * @param title - Optional title
   */
  showInfo(message: string, title?: string): void {
    const infoTitle = title || this.transloco.translate('Information');
    this.toastr.info(message, infoTitle, {
      timeOut: 3000,
      progressBar: true,
      positionClass: 'toast-top-right',
    });
  }

  /**
   * Show sync started notification
   */
  showSyncStarted(): void {
    const title = this.transloco.translate('Sync Started');
    const message = this.transloco.translate('Project synchronization has started. Please wait...');

    this.toastr.info(message, title, {
      timeOut: 3000,
      progressBar: true,
      positionClass: 'toast-top-right',
    });
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Permission Error Methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Show permission denied error
   */
  showPermissionDenied(): void {
    const title = this.transloco.translate('Permission Denied');
    const message = this.transloco.translate(
      'You do not have permission to access integration features.',
    );

    this.toastr.error(message, title, {
      timeOut: 5000,
      progressBar: true,
      positionClass: 'toast-top-right',
    });
  }

  /**
   * Show configuration error
   */
  showConfigurationError(): void {
    const title = this.transloco.translate('Configuration Error');
    const message = this.transloco.translate(
      'Integration is not properly configured. Please contact administrator.',
    );

    this.toastr.error(message, title, {
      timeOut: 6000,
      progressBar: true,
      positionClass: 'toast-top-right',
    });
  }

  showError(message: string, title?: string, timeOut?: number): void {
    const errorTitle = title || this.transloco.translate('Error');
    this.toastr.error(message, errorTitle, {
      timeOut: timeOut || 6000,
      progressBar: true,
      positionClass: 'toast-top-right',
    });
  }
}
